/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import {
  Reservation,
  ReservationMetrics,
  ReservationFilters,
  CreateReservationInput,
  UpdateReservationInput,
  ReservationTrend,
} from './types';
import { supabase } from '../../services/supabaseClient';
import { ReservationService } from '../../services/reservationService';
import { minLosService } from '../../services/minLosService';

/**
 * Monthly Revenue Data for charts
 */
export interface MonthlyRevenueData {
  month: string;
  revenue: number;
  adr: number;
  occupancy: number;
  roomNights: number;
}

/**
 * Booking Context State
 */
interface BookingState {
  reservations: Reservation[];
  metrics: ReservationMetrics | null;
  trends: ReservationTrend[];
  monthlyRevenue: MonthlyRevenueData[];
  isLoading: boolean;
  error: string | null;
  reservationService: ReservationService;
}

/**
 * Booking Context Actions
 */
type BookingAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_RESERVATIONS'; payload: Reservation[] }
  | { type: 'ADD_RESERVATION'; payload: Reservation }
  | {
      type: 'UPDATE_RESERVATION';
      payload: { id: string; updates: Partial<Reservation> };
    }
  | { type: 'DELETE_RESERVATION'; payload: string }
  | { type: 'SET_METRICS'; payload: ReservationMetrics }
  | { type: 'SET_TRENDS'; payload: ReservationTrend[] }
  | { type: 'SET_MONTHLY_REVENUE'; payload: MonthlyRevenueData[] };

/**
 * Booking Context Value
 */
interface BookingContextValue {
  // State
  reservations: Reservation[];
  metrics: ReservationMetrics | null;
  trends: ReservationTrend[];
  monthlyRevenue: MonthlyRevenueData[];
  isLoading: boolean;
  error: string | null;

  // Reservation Actions
  loadReservations: (filters: ReservationFilters) => Promise<void>;
  createReservation: (input: CreateReservationInput) => Promise<Reservation>;
  updateReservation: (
    id: string,
    updates: UpdateReservationInput
  ) => Promise<void>;
  deleteReservation: (id: string) => Promise<void>;
  cancelReservation: (id: string) => Promise<void>;

  // Metrics Actions
  loadMetrics: (propertyId: string, totalRooms: number) => Promise<void>;
  loadTrends: (propertyId: string, days: number) => Promise<void>;
  loadMonthlyRevenue: (propertyId: string, months?: number) => Promise<void>;

  // Query Actions
  getReservationById: (id: string) => Reservation | undefined;
  getReservationsByDateRange: (start: Date, end: Date) => Reservation[];
}

/**
 * Booking Reducer
 */
const bookingReducer = (
  state: BookingState,
  action: BookingAction
): BookingState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };

    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };

    case 'SET_RESERVATIONS':
      return { ...state, reservations: action.payload, isLoading: false };

    case 'ADD_RESERVATION':
      return {
        ...state,
        reservations: [...state.reservations, action.payload],
        isLoading: false,
      };

    case 'UPDATE_RESERVATION':
      return {
        ...state,
        reservations: state.reservations.map((reservation) =>
          reservation.id === action.payload.id
            ? {
                ...reservation,
                ...action.payload.updates,
                updatedAt: new Date(),
              }
            : reservation
        ),
        isLoading: false,
      };

    case 'DELETE_RESERVATION':
      return {
        ...state,
        reservations: state.reservations.filter(
          (reservation) => reservation.id !== action.payload
        ),
        isLoading: false,
      };

    case 'SET_METRICS':
      return { ...state, metrics: action.payload, isLoading: false };

    case 'SET_TRENDS':
      return { ...state, trends: action.payload, isLoading: false };

    case 'SET_MONTHLY_REVENUE':
      return { ...state, monthlyRevenue: action.payload, isLoading: false };

    default:
      return state;
  }
};

/**
 * Initial State
 */
const initialState: BookingState = {
  reservations: [],
  metrics: null,
  trends: [],
  monthlyRevenue: [],
  isLoading: false,
  error: null,
  reservationService: new ReservationService(),
};

/**
 * Booking Context
 */
const BookingContext = createContext<BookingContextValue | undefined>(
  undefined
);

/**
 * Booking Provider Component
 */
export const BookingProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(bookingReducer, initialState);
  const reservationServiceRef = useRef(state.reservationService);

  /**
   * Load Reservations
   */
  const loadReservations = useCallback(async (filters: ReservationFilters) => {
    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      const reservationData =
        await reservationServiceRef.current.getReservations(filters);

      const reservations: Reservation[] = reservationData.map((data) => ({
        id: data.id,
        roomTypeId: data.roomTypeId,
        checkIn: data.checkIn,
        checkOut: data.checkOut,
        rate: data.rate,
        guests: data.guests,
        status: data.status,
        propertyId: data.propertyId,
        guestName: data.guestName,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
      }));

      dispatch({ type: 'SET_RESERVATIONS', payload: reservations });
    } catch (error) {
      console.error('Error loading reservations:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load reservations' });
    }
  }, []);

  /**
   * Create Reservation
   */
  const createReservation = useCallback(
    async (input: CreateReservationInput): Promise<Reservation> => {
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        // Validate MinLOS requirements before creating reservation
        const minLosValidation = await minLosService.validateStayLength(
          input.propertyId,
          input.roomTypeId,
          input.checkIn,
          input.checkOut
        );

        if (!minLosValidation.isValid) {
          const errorMessage =
            minLosValidation.overrideReason ||
            `Minimum stay of ${minLosValidation.requiredMinLos} nights required`;
          dispatch({ type: 'SET_ERROR', payload: errorMessage });
          throw new Error(errorMessage);
        }

        const { data, error } = await supabase
          .from('reservations')
          .insert([
            {
              room_type_id: input.roomTypeId,
              check_in: input.checkIn.toISOString().split('T')[0],
              check_out: input.checkOut.toISOString().split('T')[0],
              rate: input.rate,
              guests: input.guests,
              property_id: input.propertyId,
              guest_name: input.guestName,
              guest_email: input.guestEmail,
              guest_phone: input.guestPhone,
              special_requests: input.specialRequests,
              source: input.source || 'manual',
              status: 'confirmed',
              created_at: new Date().toISOString(),
            },
          ])
          .select()
          .single();

        if (error) throw error;

        const reservation: Reservation = {
          id: data.id,
          roomTypeId: data.room_type_id,
          checkIn: new Date(data.check_in),
          checkOut: new Date(data.check_out),
          rate: data.rate,
          guests: data.guests,
          status: data.status,
          propertyId: data.property_id,
          // guest_name/guest_email are blanked at rest by the PII trigger.
          // The user just typed them, so echo the input — no round trip.
          guestName: input.guestName,
          guestEmail: input.guestEmail,
          guestPhone: data.guest_phone,
          specialRequests: data.special_requests,
          source: data.source,
          createdAt: new Date(data.created_at),
        };

        dispatch({ type: 'ADD_RESERVATION', payload: reservation });

        // If MinLOS override was applied, mark the gap as filled
        if (minLosValidation.gapFillOverrideApplied && minLosValidation.gapId) {
          await minLosService.markGapFilledOnOverride(
            minLosValidation.gapId,
            reservation.id,
            reservation.rate
          );
        }

        // Clear cache to force refresh on next load
        reservationServiceRef.current.clearCache(input.propertyId);

        return reservation;
      } catch (error) {
        console.error('Error creating reservation:', error);
        dispatch({
          type: 'SET_ERROR',
          payload: 'Failed to create reservation',
        });
        throw error;
      }
    },
    []
  );

  /**
   * Update Reservation
   */
  const updateReservation = useCallback(
    async (id: string, updates: UpdateReservationInput) => {
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        const { error } = await supabase
          .from('reservations')
          .update({
            room_type_id: updates.roomTypeId,
            check_in: updates.checkIn?.toISOString().split('T')[0],
            check_out: updates.checkOut?.toISOString().split('T')[0],
            rate: updates.rate,
            guests: updates.guests,
            status: updates.status,
            guest_name: updates.guestName,
            guest_email: updates.guestEmail,
            guest_phone: updates.guestPhone,
            special_requests: updates.specialRequests,
            updated_at: new Date().toISOString(),
          })
          .eq('id', id);

        if (error) throw error;

        dispatch({ type: 'UPDATE_RESERVATION', payload: { id, updates } });

        // Clear cache
        const reservation = state.reservations.find((r) => r.id === id);
        if (reservation) {
          reservationServiceRef.current.clearCache(reservation.propertyId);
        }
      } catch (error) {
        console.error('Error updating reservation:', error);
        dispatch({
          type: 'SET_ERROR',
          payload: 'Failed to update reservation',
        });
        throw error;
      }
    },
    [state.reservations]
  );

  /**
   * Delete Reservation
   */
  const deleteReservation = useCallback(
    async (id: string) => {
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        const { error } = await supabase
          .from('reservations')
          .delete()
          .eq('id', id);

        if (error) throw error;

        dispatch({ type: 'DELETE_RESERVATION', payload: id });

        // Clear cache
        const reservation = state.reservations.find((r) => r.id === id);
        if (reservation) {
          reservationServiceRef.current.clearCache(reservation.propertyId);
        }
      } catch (error) {
        console.error('Error deleting reservation:', error);
        dispatch({
          type: 'SET_ERROR',
          payload: 'Failed to delete reservation',
        });
        throw error;
      }
    },
    [state.reservations]
  );

  /**
   * Cancel Reservation
   */
  const cancelReservation = useCallback(
    async (id: string) => {
      await updateReservation(id, { status: 'cancelled' });
    },
    [updateReservation]
  );

  /**
   * Load Metrics
   */
  const loadMetrics = useCallback(
    async (propertyId: string, totalRooms: number) => {
      if (!propertyId || totalRooms <= 0) {
        dispatch({
          type: 'SET_ERROR',
          payload: 'Invalid property or room count',
        });
        return;
      }

      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        const metrics = await reservationServiceRef.current.getEnhancedMetrics(
          propertyId,
          totalRooms
        );

        if (!metrics) {
          dispatch({ type: 'SET_ERROR', payload: 'No metrics data returned' });
          return;
        }

        const sanitizeNumber = (value: number | undefined | null): number => {
          if (
            value === undefined ||
            value === null ||
            Number.isNaN(value) ||
            !Number.isFinite(value)
          ) {
            return 0;
          }
          return value;
        };

        const sanitizedMetrics = {
          ...metrics,
          occupancyRate: sanitizeNumber(metrics.occupancyRate),
          adr: sanitizeNumber(metrics.adr),
          revpar: sanitizeNumber(metrics.revpar),
          totalBookings: sanitizeNumber(metrics.totalBookings),
          occupancyChange: sanitizeNumber(metrics.occupancyChange),
          adrChange: sanitizeNumber(metrics.adrChange),
          revparChange: sanitizeNumber(metrics.revparChange),
          bookingsChange: sanitizeNumber(metrics.bookingsChange),
        };

        dispatch({ type: 'SET_METRICS', payload: sanitizedMetrics });
      } catch (error) {
        console.error('Error loading metrics:', error);
        dispatch({ type: 'SET_ERROR', payload: 'Failed to load metrics' });
      }
    },
    []
  );

  /**
   * Load Trends
   */
  const loadTrends = useCallback(async (propertyId: string, days: number) => {
    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      const trends = await reservationServiceRef.current.getReservationTrends(
        propertyId,
        days
      );

      dispatch({ type: 'SET_TRENDS', payload: trends });
    } catch (error) {
      console.error('Error loading trends:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load trends' });
    }
  }, []);

  /**
   * Load Monthly Revenue
   */
  const loadMonthlyRevenue = useCallback(
    async (propertyId: string, months: number = 12) => {
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        const revenue =
          await reservationServiceRef.current.getMonthlyRevenueTrends(
            propertyId,
            months
          );

        dispatch({ type: 'SET_MONTHLY_REVENUE', payload: revenue });
      } catch (error) {
        console.error('Error loading monthly revenue:', error);
        dispatch({
          type: 'SET_ERROR',
          payload: 'Failed to load monthly revenue',
        });
      }
    },
    []
  );

  /**
   * Get Reservation By ID
   */
  const getReservationById = useCallback(
    (id: string): Reservation | undefined => {
      return state.reservations.find((reservation) => reservation.id === id);
    },
    [state.reservations]
  );

  /**
   * Get Reservations By Date Range
   */
  const getReservationsByDateRange = useCallback(
    (start: Date, end: Date): Reservation[] => {
      return state.reservations.filter(
        (reservation) =>
          reservation.checkIn >= start && reservation.checkOut <= end
      );
    },
    [state.reservations]
  );

  const value: BookingContextValue = {
    // State
    reservations: state.reservations,
    metrics: state.metrics,
    trends: state.trends,
    monthlyRevenue: state.monthlyRevenue,
    isLoading: state.isLoading,
    error: state.error,

    // Actions
    loadReservations,
    createReservation,
    updateReservation,
    deleteReservation,
    cancelReservation,
    loadMetrics,
    loadTrends,
    loadMonthlyRevenue,
    getReservationById,
    getReservationsByDateRange,
  };

  return (
    <BookingContext.Provider value={value}>{children}</BookingContext.Provider>
  );
};

/**
 * useBooking Hook
 * Custom hook to access Booking context
 */
export const useBooking = (): BookingContextValue => {
  const context = useContext(BookingContext);

  if (context === undefined) {
    throw new Error('useBooking must be used within a BookingProvider');
  }

  return context;
};

export default BookingContext;
